import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Link, 
  Check, 
  XCircle, 
  Clock,
  Settings,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Zap,
  Building,
  Globe,
  Key,
  Play,
  Pause,
  RotateCcw,
  Activity,
  Server,
  Database
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Integrations = () => {
  const [activeSection, setActiveSection] = useState('workflow');
  const [integrations, setIntegrations] = useState({});
  const [showApiKeys, setShowApiKeys] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    fetchIntegrations();
    fetchConnectionStatus();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/api/integrations/list`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      toast.error('Failed to fetch integrations');
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/integrations/status`);
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Error fetching connection status:', error);
    }
  };

  const testConnection = async (integrationId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/integrations/test-connection`, {
        integrationId
      });
      
      setTestResults(prev => ({
        ...prev,
        [integrationId]: response.data
      }));
      
      if (response.data.success) {
        toast.success(`${integrationId} connection successful!`);
      } else {
        toast.error(`${integrationId} connection failed: ${response.data.error}`);
      }
      
      fetchConnectionStatus();
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(`Failed to test ${integrationId} connection`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateIntegration = async (integrationId, config) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/integrations/update`, {
        integrationId,
        config
      });
      
      toast.success(`${integrationId} configuration updated`);
      fetchIntegrations();
      fetchConnectionStatus();
    } catch (error) {
      console.error('Error updating integration:', error);
      toast.error(`Failed to update ${integrationId} configuration`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleIntegration = async (integrationId, enabled) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/integrations/toggle`, {
        integrationId,
        enabled
      });
      
      toast.success(`${integrationId} ${enabled ? 'enabled' : 'disabled'}`);
      fetchIntegrations();
      fetchConnectionStatus();
    } catch (error) {
      console.error('Error toggling integration:', error);
      toast.error(`Failed to ${enabled ? 'enable' : 'disable'} ${integrationId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const syncData = async (integrationId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/integrations/sync`, {
        integrationId
      });
      
      toast.success(`${integrationId} sync completed: ${response.data.recordsProcessed} records`);
    } catch (error) {
      console.error('Error syncing data:', error);
      toast.error(`Failed to sync ${integrationId} data`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const sections = [
    { id: 'workflow', label: 'Workflow Engines', icon: Building },
    { id: 'vendors', label: 'Vendor Portals', icon: ExternalLink },
    { id: 'jobboards', label: 'Job Boards', icon: Globe },
    { id: 'monitoring', label: 'Monitoring', icon: Activity },
  ];

  // Workflow Engine Integrations
  const WorkflowEngines = () => (
    <div className="space-y-6">
      {/* ServiceNow Integration */}
      <ServiceNowIntegration />
      
      {/* Workday Integration */}
      <WorkdayIntegration />
    </div>
  );

  // Vendor Portal Integrations
  const VendorPortalIntegrations = () => (
    <div className="space-y-6">
      {/* Beeline Integration */}
      <BeelineIntegration />
      
      {/* Agile One Integration */}
      <AgileOneIntegration />
      
      {/* SAP FieldGlass Integration */}
      <SAPFieldGlassIntegration />
    </div>
  );

  // ServiceNow Integration Component
  const ServiceNowIntegration = () => {
    const [config, setConfig] = useState({
      instanceUrl: integrations.servicenow?.instanceUrl || '',
      username: integrations.servicenow?.username || '',
      password: integrations.servicenow?.password || '',
      table: integrations.servicenow?.table || 'x_custom_candidates',
      enabled: integrations.servicenow?.enabled || false
    });

    const handleSave = () => {
      updateIntegration('servicenow', config);
    };

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Server className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">ServiceNow</h3>
                <p className="text-sm text-gray-600">IT Service Management & HR Workflows</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.servicenow?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('servicenow', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance URL
              </label>
              <input
                type="url"
                placeholder="https://your-instance.service-now.com"
                value={config.instanceUrl}
                onChange={(e) => setConfig({ ...config, instanceUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Table
              </label>
              <select
                value={config.table}
                onChange={(e) => setConfig({ ...config, table: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="x_custom_candidates">Custom Candidates Table</option>
                <option value="hr_profile">HR Profile</option>
                <option value="sn_hr_core_profile">HR Core Profile</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                placeholder="ServiceNow username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.servicenow ? 'text' : 'password'}
                  placeholder="ServiceNow password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, servicenow: !showApiKeys.servicenow })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.servicenow ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>
          
          {connectionStatus.servicenow?.lastSync && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Last sync: {new Date(connectionStatus.servicenow.lastSync).toLocaleString()}
                {connectionStatus.servicenow.recordsProcessed && (
                  <span> • {connectionStatus.servicenow.recordsProcessed} records processed</span>
                )}
              </p>
            </div>
          )}
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
            
            <button
              onClick={() => testConnection('servicenow')}
              disabled={isLoading || !config.instanceUrl || !config.username || !config.password}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>Test Connection</span>
            </button>
            
            {config.enabled && connectionStatus.servicenow?.status === 'connected' && (
              <button
                onClick={() => syncData('servicenow')}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Sync Now</span>
              </button>
            )}
          </div>
          
          {testResults.servicenow && (
            <div className={`mt-4 p-4 rounded-lg ${
              testResults.servicenow.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                {testResults.servicenow.success ? 
                  <Check className="h-5 w-5 text-green-600" /> :
                  <XCircle className="h-5 w-5 text-red-600" />
                }
                <span className={`font-medium ${testResults.servicenow.success ? 'text-green-800' : 'text-red-800'}`}>
                  Connection {testResults.servicenow.success ? 'Successful' : 'Failed'}
                </span>
              </div>
              <p className={`text-sm ${testResults.servicenow.success ? 'text-green-700' : 'text-red-700'}`}>
                {testResults.servicenow.message}
              </p>
              {testResults.servicenow.details && (
                <div className="mt-2 text-xs text-gray-600">
                  <p>Response time: {testResults.servicenow.details.responseTime}ms</p>
                  <p>Instance version: {testResults.servicenow.details.version}</p>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Workflow Capabilities</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Automatic candidate record creation in ServiceNow</li>
              <li>• Job requisition workflow integration</li>
              <li>• Status sync between systems</li>
              <li>• Automated approval processes</li>
              <li>• Candidate onboarding workflow triggers</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Workday Integration Component  
  const WorkdayIntegration = () => {
    const [config, setConfig] = useState({
      tenantUrl: integrations.workday?.tenantUrl || '',
      username: integrations.workday?.username || '',
      password: integrations.workday?.password || '',
      clientId: integrations.workday?.clientId || '',
      clientSecret: integrations.workday?.clientSecret || '',
      enabled: integrations.workday?.enabled || false
    });

    const handleSave = () => {
      updateIntegration('workday', config);
    };

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Building className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Workday</h3>
                <p className="text-sm text-gray-600">Human Capital Management & Recruiting</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.workday?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('workday', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant URL
              </label>
              <input
                type="url"
                placeholder="https://your-tenant.workday.com"
                value={config.tenantUrl}
                onChange={(e) => setConfig({ ...config, tenantUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                placeholder="Workday username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.workday ? 'text' : 'password'}
                  placeholder="Workday password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, workday: !showApiKeys.workday })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.workday ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                placeholder="OAuth Client ID"
                value={config.clientId}
                onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.workdaySecret ? 'text' : 'password'}
                  placeholder="OAuth Client Secret"
                  value={config.clientSecret}
                  onChange={(e) => setConfig({ ...config, clientSecret: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, workdaySecret: !showApiKeys.workdaySecret })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.workdaySecret ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
            
            <button
              onClick={() => testConnection('workday')}
              disabled={isLoading || !config.tenantUrl || !config.username || !config.password}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>Test Connection</span>
            </button>
            
            {config.enabled && connectionStatus.workday?.status === 'connected' && (
              <button
                onClick={() => syncData('workday')}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Sync Now</span>
              </button>
            )}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Workflow Capabilities</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Job requisition creation and approval workflows</li>
              <li>• Candidate data sync to Workday recruiting</li>
              <li>• Automated offer letter generation</li>
              <li>• Onboarding workflow triggers</li>
              <li>• Position management integration</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Beeline Integration Component
  const BeelineIntegration = () => {
    const [config, setConfig] = useState({
      instanceUrl: integrations.beeline?.instanceUrl || '',
      clientId: integrations.beeline?.clientId || '',
      clientSecret: integrations.beeline?.clientSecret || '',
      username: integrations.beeline?.username || '',
      password: integrations.beeline?.password || '',
      enabled: integrations.beeline?.enabled || false
    });

    const handleSave = () => {
      updateIntegration('beeline', config);
    };

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ExternalLink className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Beeline</h3>
                <p className="text-sm text-gray-600">Vendor Management System & Contingent Workforce</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.beeline?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('beeline', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance URL
              </label>
              <input
                type="url"
                placeholder="https://your-instance.beeline.com"
                value={config.instanceUrl}
                onChange={(e) => setConfig({ ...config, instanceUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                placeholder="Beeline username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.beeline ? 'text' : 'password'}
                  placeholder="Beeline password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, beeline: !showApiKeys.beeline })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.beeline ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                placeholder="OAuth Client ID"
                value={config.clientId}
                onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
              Save Configuration
            </button>
            
            <button
              onClick={() => testConnection('beeline')}
              disabled={isLoading || !config.enabled}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              <Zap className="h-4 w-4 mr-2" />
              Test Connection
            </button>
            
            {config.enabled && (
              <button
                onClick={() => syncData('beeline')}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                <span>Sync Data</span>
              </button>
            )}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Integration Capabilities</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Job requisition sync from Beeline to Juno Talent Assist</li>
              <li>• Candidate submission and status updates</li>
              <li>• Automated timesheet and invoice processing</li>
              <li>• SOW (Statement of Work) management integration</li>
              <li>• Compliance and vendor management workflows</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Agile One Integration Component
  const AgileOneIntegration = () => {
    const [config, setConfig] = useState({
      instanceUrl: integrations.agileone?.instanceUrl || '',
      apiKey: integrations.agileone?.apiKey || '',
      username: integrations.agileone?.username || '',
      password: integrations.agileone?.password || '',
      enabled: integrations.agileone?.enabled || false
    });

    const handleSave = () => {
      updateIntegration('agileone', config);
    };

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Building className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Agile One</h3>
                <p className="text-sm text-gray-600">Contingent Workforce Management Platform</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.agileone?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('agileone', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance URL
              </label>
              <input
                type="url"
                placeholder="https://your-company.agileone.com"
                value={config.instanceUrl}
                onChange={(e) => setConfig({ ...config, instanceUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.agileone ? 'text' : 'password'}
                  placeholder="Agile One API Key"
                  value={config.apiKey}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, agileone: !showApiKeys.agileone })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.agileone ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                placeholder="Agile One username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.agileone_pw ? 'text' : 'password'}
                  placeholder="Agile One password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, agileone_pw: !showApiKeys.agileone_pw })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.agileone_pw ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
              Save Configuration
            </button>
            
            <button
              onClick={() => testConnection('agileone')}
              disabled={isLoading || !config.enabled}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              <Zap className="h-4 w-4 mr-2" />
              Test Connection
            </button>
            
            {config.enabled && (
              <button
                onClick={() => syncData('agileone')}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                <span>Sync Data</span>
              </button>
            )}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Integration Capabilities</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Real-time job requisition synchronization</li>
              <li>• Candidate profile and status management</li>
              <li>• Automated contract and rate management</li>
              <li>• Performance tracking and reporting</li>
              <li>• Compliance monitoring and alerts</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // SAP FieldGlass Integration Component
  const SAPFieldGlassIntegration = () => {
    const [config, setConfig] = useState({
      instanceUrl: integrations.sapfieldglass?.instanceUrl || '',
      clientId: integrations.sapfieldglass?.clientId || '',
      clientSecret: integrations.sapfieldglass?.clientSecret || '',
      username: integrations.sapfieldglass?.username || '',
      password: integrations.sapfieldglass?.password || '',
      enabled: integrations.sapfieldglass?.enabled || false
    });

    const handleSave = () => {
      updateIntegration('sapfieldglass', config);
    };

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">SAP FieldGlass</h3>
                <p className="text-sm text-gray-600">Vendor Management & External Workforce</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.sapfieldglass?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('sapfieldglass', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance URL
              </label>
              <input
                type="url"
                placeholder="https://your-instance.fieldglass.net"
                value={config.instanceUrl}
                onChange={(e) => setConfig({ ...config, instanceUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                placeholder="SAP FieldGlass username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.sapfieldglass ? 'text' : 'password'}
                  placeholder="SAP FieldGlass password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, sapfieldglass: !showApiKeys.sapfieldglass })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.sapfieldglass ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                placeholder="OAuth Client ID"
                value={config.clientId}
                onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
              Save Configuration
            </button>
            
            <button
              onClick={() => testConnection('sapfieldglass')}
              disabled={isLoading || !config.enabled}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              <Zap className="h-4 w-4 mr-2" />
              Test Connection
            </button>
            
            {config.enabled && (
              <button
                onClick={() => syncData('sapfieldglass')}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                <span>Sync Data</span>
              </button>
            )}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Integration Capabilities</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Enterprise-grade vendor and supplier management</li>
              <li>• Automated job requisition and approval workflows</li>
              <li>• Comprehensive candidate tracking and onboarding</li>
              <li>• Advanced analytics and spend management</li>
              <li>• Compliance and risk management automation</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Job Board Integrations
  const JobBoardIntegrations = () => (
    <div className="space-y-6">
      <LinkedInIntegration />
      <IndeedIntegration />  
      <MonsterIntegration />
    </div>
  );

  const LinkedInIntegration = () => {
    const [config, setConfig] = useState({
      apiKey: integrations.linkedin?.apiKey || '',
      clientId: integrations.linkedin?.clientId || '',
      clientSecret: integrations.linkedin?.clientSecret || '',
      enabled: integrations.linkedin?.enabled || false
    });

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ExternalLink className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">LinkedIn Recruiter</h3>
                <p className="text-sm text-gray-600">Professional networking and recruiting</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.linkedin?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('linkedin', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-800">LinkedIn API Access Required</span>
            </div>
            <p className="text-sm text-blue-700">
              LinkedIn Recruiter API access requires a LinkedIn Talent Solutions license. 
              <a href="https://business.linkedin.com/talent-solutions" target="_blank" rel="noopener noreferrer" 
                 className="underline hover:no-underline ml-1">
                Learn more about LinkedIn Talent Solutions
              </a>
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                placeholder="LinkedIn App Client ID"
                value={config.clientId}
                onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.linkedin ? 'text' : 'password'}
                  placeholder="LinkedIn App Client Secret"
                  value={config.clientSecret}
                  onChange={(e) => setConfig({ ...config, clientSecret: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, linkedin: !showApiKeys.linkedin })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.linkedin ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key (Optional - for enhanced features)
              </label>
              <div className="relative">
                <input
                  type={showApiKeys.linkedinApi ? 'text' : 'password'}
                  placeholder="LinkedIn Recruiter API Key"
                  value={config.apiKey}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys({ ...showApiKeys, linkedinApi: !showApiKeys.linkedinApi })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKeys.linkedinApi ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => updateIntegration('linkedin', config)}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
            
            <button
              onClick={() => testConnection('linkedin')}
              disabled={isLoading || !config.clientId || !config.clientSecret}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>Test Connection</span>
            </button>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Integration Features</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Profile search and candidate discovery</li>
              <li>• InMail messaging automation</li>
              <li>• Job posting to LinkedIn</li>
              <li>• Candidate pipeline sync</li>
              <li>• Analytics and reporting</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const IndeedIntegration = () => {
    const [config, setConfig] = useState({
      publisherKey: integrations.indeed?.publisherKey || '',
      enabled: integrations.indeed?.enabled || false
    });

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Database className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Indeed</h3>
                <p className="text-sm text-gray-600">Job search and candidate sourcing</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.indeed?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('indeed', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Publisher Key
            </label>
            <div className="relative">
              <input
                type={showApiKeys.indeed ? 'text' : 'password'}
                placeholder="Indeed Publisher API Key"
                value={config.publisherKey}
                onChange={(e) => setConfig({ ...config, publisherKey: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowApiKeys({ ...showApiKeys, indeed: !showApiKeys.indeed })}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showApiKeys.indeed ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Get your Publisher Key from <a href="https://ads.indeed.com/jobroll/xmlfeed" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Indeed Publisher Portal</a>
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => updateIntegration('indeed', config)}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
            
            <button
              onClick={() => testConnection('indeed')}
              disabled={isLoading || !config.publisherKey}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>Test Connection</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const MonsterIntegration = () => {
    const [config, setConfig] = useState({
      apiKey: integrations.monster?.apiKey || '',
      enabled: integrations.monster?.enabled || false
    });

    return (
      <div className="bg-white rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Globe className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Monster</h3>
                <p className="text-sm text-gray-600">Global job board and talent network</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(connectionStatus.monster?.status)}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setConfig({ ...config, enabled: e.target.checked });
                    toggleIntegration('monster', e.target.checked);
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.enabled ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <div className="relative">
              <input
                type={showApiKeys.monster ? 'text' : 'password'}
                placeholder="Monster API Key"
                value={config.apiKey}
                onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowApiKeys({ ...showApiKeys, monster: !showApiKeys.monster })}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showApiKeys.monster ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
              </button>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => updateIntegration('monster', config)}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
            
            <button
              onClick={() => testConnection('monster')}
              disabled={isLoading || !config.apiKey}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>Test Connection</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Integration Monitoring Dashboard
  const IntegrationMonitoring = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Integration Status Overview</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Object.entries(connectionStatus).map(([integration, status]) => (
            <div key={integration} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium capitalize">{integration}</span>
                {getStatusIcon(status.status)}
              </div>
              <p className="text-sm text-gray-600 mb-2">{status.message || 'No recent activity'}</p>
              {status.lastSync && (
                <p className="text-xs text-gray-500">
                  Last sync: {new Date(status.lastSync).toLocaleString()}
                </p>
              )}
            </div>
          ))}
        </div>
        
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Recent Integration Activity</h4>
          <div className="space-y-2">
            {connectionStatus.recentActivity ? connectionStatus.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">{activity.integration}</span>
                  <span className="text-gray-600 ml-2">{activity.action}</span>
                </div>
                <span className="text-sm text-gray-500">{new Date(activity.timestamp).toLocaleString()}</span>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3">
          <Link className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
            <p className="text-gray-600">Manage workflow engines and job board integrations</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeSection === 'workflow' && <WorkflowEngines />}
          {activeSection === 'vendors' && <VendorPortalIntegrations />}
          {activeSection === 'jobboards' && <JobBoardIntegrations />}
          {activeSection === 'monitoring' && <IntegrationMonitoring />}
        </div>
      </div>
    </div>
  );
};

export default Integrations;